/**
 * Массив хозяйства в строке реестра (07.10.2026).
 *
 * Хозяйство часто держит землю на нескольких массивах, и список через запятую
 * растягивал строку на 2–4 строки. Массив — фильтр, а не уровень учёта
 * хозяйства (screen-levels.md), поэтому в строке — ОСНОВНОЙ массив (где больше
 * всего земли хозяйства, журнал №66), а остальные — по нажатию «+N» со
 * списком и гектарами. Фильтр «Массив» по-прежнему находит хозяйство по
 * ЛЮБОМУ его массиву.
 */
import { Button, ButtonContext, Dialog, DialogTrigger, Popover } from 'react-aria-components';
import styles from '../../components/controls.module.css';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { haUnit } from '../lib/format';
import { massifLabel } from './district/model';

export interface MassifLand {
  id: string;
  name: string;
  /** Земля хозяйства на массиве по контурам реестра; `null` — не названа. */
  ha: number | null;
}

const m = defineMessages({
  noArea: { id: 'massif.cell.noArea', defaultMessage: 'площадь не названа', description: 'Земля хозяйства на массиве в списке массивов хозяйства: площадь не названа в реестре' },
  none: { id: 'massif.cell.none', defaultMessage: 'не назван', description: 'Ячейка «Массив» в реестре хозяйств: у хозяйства массив не назван' },
  moreAria: {
    id: 'massif.cell.moreAria',
    defaultMessage: 'Ещё {count, plural, one {# массив} few {# массива} many {# массивов} other {# массива}} хозяйства {farm}',
    description: 'aria-label кнопки «+N» в ячейке «Массив»: открыть остальные массивы хозяйства; {count} — сколько массивов, {farm} — название хозяйства',
  },
  listAria: { id: 'massif.cell.listAria', defaultMessage: 'Массивы хозяйства {farm}', description: 'aria-label окна со списком массивов хозяйства; {farm} — название хозяйства' },
  listTitle: { id: 'massif.cell.listTitle', defaultMessage: 'Земля хозяйства по массивам', description: 'Заголовок окна со списком массивов хозяйства и земли на каждом' },
  main: { id: 'massif.cell.main', defaultMessage: '{massif} · основной', description: 'Строка в окне массивов хозяйства: основной массив (где больше всего земли хозяйства); {massif} — название массива' },
});

export function MassifCell({ list, farmName }: { list: readonly MassifLand[]; farmName: string }) {
  const t = useT();
  const haText = (v: number | null) => (v === null ? t(m.noArea) : haUnit(Math.round(v * 10) / 10));
  if (!list.length) return <span className="t-caption">{t(m.none)}</span>;
  const [main, ...rest] = list;
  return (
    <span className="massif-cell">
      <span className="massif-cell__main">{massifLabel(main!.name)}</span>
      {rest.length ? (
        <ButtonContext.Provider value={{}}>
          <DialogTrigger>
            <Button className="massif-cell__more" aria-label={t(m.moreAria, { count: rest.length, farm: farmName })}>
              +{rest.length}
            </Button>
            <Popover className={styles.infoPopover} placement="bottom start" offset={4}>
              <Dialog className={styles.infoDialog} aria-label={t(m.listAria, { farm: farmName })}>
                <div className={styles.infoTitle}>{t(m.listTitle)}</div>
                <ul className="massif-cell__list">
                  {list.map((land, i) => (
                    <li key={land.id}>
                      <span>{i === 0 ? t(m.main, { massif: massifLabel(land.name) }) : massifLabel(land.name)}</span>
                      <strong>{haText(land.ha)}</strong>
                    </li>
                  ))}
                </ul>
              </Dialog>
            </Popover>
          </DialogTrigger>
        </ButtonContext.Provider>
      ) : null}
    </span>
  );
}
